import React, { useState } from "react";
import {
  useGetCustomerOrdersQuery,
  useGetOrderDetailsQuery,
} from "../slices/orderSlice";
import Pagination from "../components/utilComponents/Pagination";
import Modal from "../components/layout/Modal";

const Orders = () => {
  const [page, setPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading, error } = useGetCustomerOrdersQuery({
    page,
    limit: 10,
  });

  const { data: orderDetails, isLoading: isOrderDetailsLoading } =
    useGetOrderDetailsQuery(selectedOrderId, { skip: !selectedOrderId });

  const handleSeeDetails = (orderId) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };

  const renderOrderItems = (items) =>
    items.map((item) => (
      <div
        key={item._id}
        className="flex items-center justify-between border p-4 rounded-md mb-4"
      >
        <div className="flex items-center space-x-4">
          <img
            src={item.item?.images?.[0]}
            alt={item.item?.title || "Product Image"}
            className="w-16 h-16 object-cover rounded-md"
          />
          <div>
            <h4 className="font-semibold">
              {item.item?.title || "Unknown Item"}
            </h4>
            <p className="text-sm text-gray-600">by {item.vendor.storeName}</p>
            <p className="text-xs text-gray-400">Qty: {item.qty}</p>
            <p className="text-xs text-gray-400">
              Price: ₦{item.price.toLocaleString()}
            </p>
          </div>
        </div>
        <button
          className="text-orange-600"
          onClick={() => handleSeeDetails(item.orderId)}
        >
          See Details
        </button>
      </div>
    ));

  const renderModalContent = () => {
    if (isOrderDetailsLoading) {
      return <p>Loading order details...</p>;
    }

    if (!orderDetails) {
      return <p>Order details not found.</p>;
    }

    return (
      <div>
        {/* Order Header */}
        <div className="mb-6">
          <h2 className="text-lg font-medium mb-2">
            Order #{orderDetails.orderId}
          </h2>
          <p className="text-sm text-gray-600">
            Placed on {new Date(orderDetails.createdAt).toLocaleDateString()}
          </p>
          <p className="text-sm text-gray-600">
            Total: ₦{orderDetails.total.toLocaleString()}
          </p>
          <p>status: {orderDetails.status}</p>
          <p>payment status: {orderDetails.paymentStatus}</p>
        </div>

        {/* Items Section */}
        <div className="mb-6">
          <h3 className="text-md font-semibold mb-2">Items in Your Order:</h3>
          {orderDetails.orderItems.map((item) => (
            <div
              key={item._id}
              className="flex items-center justify-between border p-4 rounded-md mb-4"
            >
              <div className="flex items-center space-x-4">
                <img
                  src={item.item?.images?.[0]}
                  alt={item.item?.title || "Product Image"}
                  className="w-16 h-16 object-cover rounded-md"
                />
                <div>
                  <h4 className="font-semibold">{item.item?.title}</h4>
                  <p className="text-sm text-gray-600">
                    by {item.vendor.storeName}
                  </p>
                  <p className="text-xs text-gray-400">Qty: {item.qty}</p>
                  <p className="text-xs text-gray-400">
                    Price: ₦{item.price.toLocaleString()}
                  </p>
                </div>
              </div>
              <div>
                {/* <button className="bg-orange-600 text-white py-2 px-4 rounded-md">
                  Buy Again
                </button> */}
              </div>
            </div>
          ))}
        </div>

        {/* Payment Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Payment Information */}
          <div className="bg-gray-100 p-4 rounded-md">
            <h3 className="text-md font-semibold mb-2">Payment Information</h3>

            <p className="text-sm text-gray-600">
              <strong>Sub Total:</strong> ₦
              {orderDetails.subTotal.toLocaleString()}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Shipping:</strong> ₦
              {orderDetails.shipping.toLocaleString()}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Tax:</strong> ₦{orderDetails.tax.toLocaleString()}
            </p>
            <p className="text-sm font-semibold">
              <strong>Total:</strong> ₦{orderDetails.total.toLocaleString()}
            </p>
          </div>

          {/* Delivery Information */}
          <div className="bg-gray-100 p-4 rounded-md">
            <h3 className="text-md font-semibold mb-2">Delivery Information</h3>

            <p className="text-sm text-gray-600">
              <strong>Shipping Address</strong> <br />
              {orderDetails.shippingInfo.firstName}{" "}
              {orderDetails.shippingInfo.lastName} <br />
              <p></p>
              {orderDetails.shippingInfo.address},{" "}
              {orderDetails.shippingInfo.area},{" "}
              {orderDetails.shippingInfo.state},{" "}
              {orderDetails.shippingInfo.postcode}
            </p>
            <p className="text-sm text-gray-600">
              {orderDetails.shippingInfo.phoneNumber}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Utility function for status colors
  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "orange-500";
      case "processing":
        return "blue-500";
      case "shipped":
        return "purple-500";
      case "delivered":
        return "green-500";
      case "cancelled":
        return "red-500";
      default:
        return "gray-500";
    }
  };

  return (
    <div className="mx-auto">
      <h1 className="text-xl font-semibold mb-4">My Orders</h1>
      {data?.items?.length > 0 ? (
        <div>
          {data.items.map((item) => renderOrderItems([item]))}
          <Pagination
            currentPage={data.currentPage}
            totalPages={data.totalPages}
            onPageChange={setPage}
          />
        </div>
      ) : (
        <p className="text-gray-500">No orders found.</p>
      )}
      <Modal isVisible={isModalOpen} onClose={() => setIsModalOpen(false)}>
        {renderModalContent()}
      </Modal>
    </div>
  );
};

export default Orders;
